import { memo, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorBoundary from "@/components/layout/components/ErrorBoundary";
import {
  FilePreviewContent,
  getDialogClassName,
  getDialogContainerClassName,
} from "./FilePreviewContent";
import { FilePreviewErrorState } from "./FilePreviewErrorState";

interface FilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName?: string;
  mimeType?: string;
}

export const FilePreviewDialog = memo(
  ({ open, onClose, fileUrl, fileName, mimeType }: FilePreviewDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();

    const displayFileName = useMemo(
      () => fileName || t("filePreview.previewFile"),
      [fileName, t],
    );

    const handleError = useCallback(
      (error: Error) => {
        toast({
          title: t("filePreview.errorLoadingPdf"),
          description: error.message || t("filePreview.renderFileError"),
          variant: "destructive",
        });
      },
      [toast, t],
    );

    const errorFallback = useMemo(
      () => (
        <FilePreviewErrorState
          title={t("filePreview.errorLoadingPdf")}
          message={t("filePreview.renderFileError")}
          isFullScreen={true}
        />
      ),
      [t],
    );

    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className={getDialogClassName(fileUrl, fileName, mimeType)}
        >
          <DialogHeader className="flex-shrink-0 pr-10 min-w-0 space-y-0">
            <DialogTitle
              className="truncate min-w-0 max-w-full leading-normal"
              title={fileName}
            >
              {displayFileName}
            </DialogTitle>
          </DialogHeader>

          {fileUrl && (
            <div
              className={getDialogContainerClassName(
                fileUrl,
                fileName,
                mimeType,
              )}
            >
              <ErrorBoundary onError={handleError} fallback={errorFallback}>
                <FilePreviewContent
                  fileUrl={fileUrl}
                  fileName={fileName}
                  mimeType={mimeType}
                  isFullScreen={true}
                />
              </ErrorBoundary>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);
